import React, { useEffect, useState } from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import {
  AutoAwesome as DraftIcon,
  ThumbUp as ApprovedIcon,
  Send as PostedIcon,
  Cancel as RejectedIcon,
  Block as VoidIcon,
} from '@mui/icons-material';
import { userAPI } from '../../../services/api';
import { LedgerPicker } from '../LedgerPickers';

// ---------------------------------------------------------------------------
// Shared Expenses UI kit - mirrors receivables/ArShared.js and
// payables/ApShared.js so this module reads as one product with the rest
// of the ledger.
// ---------------------------------------------------------------------------

export const getErrorMessage = (error, fallback = 'Operation failed') =>
  error?.response?.data?.message || fallback;

export const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

export const unwrapList = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : payload.items ?? [];
};

export const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? String(d)
    : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_CFG = {
  DRAFT: { label: 'Draft', color: 'default', Icon: DraftIcon },
  APPROVED: { label: 'Approved', color: 'success', Icon: ApprovedIcon },
  POSTED: { label: 'Posted', color: 'info', Icon: PostedIcon },
  REJECTED: { label: 'Rejected', color: 'error', Icon: RejectedIcon },
  VOID: { label: 'Void', color: 'error', Icon: VoidIcon },
};

export const ExpenseStatusChip = ({ status }) => {
  const st = String(status || 'DRAFT').toUpperCase();
  const cfg = STATUS_CFG[st] || { label: status || 'Draft', color: 'default', Icon: DraftIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

export const StatTile = ({ label, value, color }) => (
  <Box sx={{ flex: '1 1 140px', textAlign: 'center', py: 1.5, px: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={700} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

export const StatStrip = ({ tiles = [] }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    divider={<Divider orientation="vertical" flexItem />}
    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}
  >
    {tiles.map((t) => (
      <StatTile key={t.label} {...t} />
    ))}
  </Stack>
);

// Employee picker for expense claims: sourced from the school's own staff
// directory (GET /users - non-ledger), not a ledger party-sync lookup -
// config/ledger.php's 'expenses' section doesn't whitelist party-sync/users
// (only 'receivables' does), so there is no ledger-side employee lookup to
// call. The raw schoolsync user id is sent as employee_id, the same
// "borrow the school's own id space" convention SchoolAccounting.php uses
// for customer_id = students.id.
let employeeCache = null;
export const EmployeePicker = ({ label = 'Employee', value, onChange, required, disabled, helper }) => {
  const [options, setOptions] = useState(employeeCache || []);
  const [loading, setLoading] = useState(!employeeCache);

  useEffect(() => {
    if (employeeCache) return;
    let alive = true;
    userAPI.getAll({ per_page: 100 })
      .then((res) => {
        if (!alive) return;
        const payload = res?.data?.data ?? res?.data ?? [];
        const list = Array.isArray(payload) ? payload : payload.data ?? [];
        const opts = list.map((u) => ({ value: u.id, label: u.name || `User ${u.id}` }));
        employeeCache = opts;
        setOptions(opts);
      })
      .catch(() => setOptions([]))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <LedgerPicker
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      loading={loading}
      required={required}
      disabled={disabled}
      helper={helper}
    />
  );
};
